import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Reportes() {
  const navigate = useNavigate();
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fmt = n => `$${(n||0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

  async function cargarDatos() {
    if (!fechaDesde || !fechaHasta) {
      alert('Seleccioná fecha desde y hasta');
      return;
    }
    setLoading(true);

    const [{ data: ventas }, { data: gastos }] = await Promise.all([
      supabase.from('caja_diaria').select('*').gte('fecha', fechaDesde).lte('fecha', fechaHasta).order('fecha'),
      supabase.from('gastos_diarios').select('*').gte('fecha', fechaDesde).lte('fecha', fechaHasta).order('fecha'),
    ]);

    const totalVentas = (ventas||[]).reduce((sum, r) =>
      sum + (r.efectivo||0) + (r.mercadopago||0) + (r.pedidosya||0) + (r.rappi||0) + (r.otros||0), 0);
    const totalGastos = (gastos||[]).reduce((sum, g) => sum + (g.monto||0), 0);

    // Totales por medio de pago
    const medios = { efectivo:0, mercadopago:0, pedidosya:0, rappi:0, otros:0 };
    (ventas||[]).forEach(r => {
      medios.efectivo    += r.efectivo    || 0;
      medios.mercadopago += r.mercadopago || 0;
      medios.pedidosya   += r.pedidosya   || 0;
      medios.rappi       += r.rappi       || 0;
      medios.otros       += r.otros       || 0;
    });

    // Gastos por categoría
    const porCategoria = {};
    (gastos||[]).forEach(g => {
      porCategoria[g.categoria] = (porCategoria[g.categoria] || 0) + g.monto;
    });

    // Resumen por fecha
    const fechas = [...new Set([...(ventas||[]).map(r=>r.fecha), ...(gastos||[]).map(g=>g.fecha)])].sort();
    const porFecha = fechas.map(f => {
      const ingDia = (ventas||[]).filter(r=>r.fecha===f).reduce((s,r) =>
        s+(r.efectivo||0)+(r.mercadopago||0)+(r.pedidosya||0)+(r.rappi||0)+(r.otros||0),0);
      const gastDia = (gastos||[]).filter(g=>g.fecha===f).reduce((s,g)=>s+(g.monto||0),0);
      return { fecha:f, ingresos:ingDia, gastos:gastDia, balance:ingDia-gastDia };
    });

    setPreview({ totalVentas, totalGastos, balance: totalVentas-totalGastos, medios, porCategoria, porFecha, ventas, gastos });
    setLoading(false);
  }

  function generarPDF() {
    if (!preview) return;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();

    // Título
    doc.setFillColor(27, 38, 49);
    doc.rect(0, 0, pageW, 40, 'F');
    doc.setTextColor(255,255,255);
    doc.setFontSize(20);
    doc.setFont('helvetica','bold');
    doc.text('LOCAL DE COMIDAS', pageW/2, 18, { align:'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica','normal');
    doc.text(`Reporte del ${fechaDesde} al ${fechaHasta}`, pageW/2, 28, { align:'center' });
    doc.setFontSize(9);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-AR')}`, pageW/2, 36, { align:'center' });

    // KPIs
    doc.setTextColor(0,0,0);
    doc.setFontSize(13);
    doc.setFont('helvetica','bold');
    doc.text('RESUMEN GENERAL', 14, 52);

    const kpis = [
      ['💰 Total Ingresos', fmt(preview.totalVentas)],
      ['💸 Total Gastos',   fmt(preview.totalGastos)],
      ['📊 Balance Neto',   fmt(preview.balance)],
    ];
    autoTable(doc, {
      startY: 56,
      head: [['Concepto', 'Monto']],
      body: kpis,
      theme: 'grid',
      headStyles: { fillColor: [230,126,34], textColor:255, fontStyle:'bold' },
      alternateRowStyles: { fillColor: [253,235,208] },
      styles: { fontSize:11, cellPadding:5 },
      columnStyles: { 1: { halign:'right', fontStyle:'bold' } },
    });

    // Medios de pago
    let y = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(13);
    doc.setFont('helvetica','bold');
    doc.text('INGRESOS POR MEDIO DE PAGO', 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [['Medio de Pago', 'Total', '% del Total']],
      body: [
        ['💵 Efectivo',    fmt(preview.medios.efectivo),    preview.totalVentas ? `${((preview.medios.efectivo/preview.totalVentas)*100).toFixed(1)}%` : '0%'],
        ['📱 MercadoPago', fmt(preview.medios.mercadopago), preview.totalVentas ? `${((preview.medios.mercadopago/preview.totalVentas)*100).toFixed(1)}%` : '0%'],
        ['🛵 PedidosYa',   fmt(preview.medios.pedidosya),   preview.totalVentas ? `${((preview.medios.pedidosya/preview.totalVentas)*100).toFixed(1)}%` : '0%'],
        ['🟠 Rappi',       fmt(preview.medios.rappi),       preview.totalVentas ? `${((preview.medios.rappi/preview.totalVentas)*100).toFixed(1)}%` : '0%'],
        ['➕ Otros',       fmt(preview.medios.otros),       preview.totalVentas ? `${((preview.medios.otros/preview.totalVentas)*100).toFixed(1)}%` : '0%'],
      ],
      theme: 'grid',
      headStyles: { fillColor: [27,38,49], textColor:255, fontStyle:'bold' },
      alternateRowStyles: { fillColor: [240,244,248] },
      styles: { fontSize:10, cellPadding:4 },
      columnStyles: { 1: { halign:'right' }, 2: { halign:'center' } },
    });

    // Gastos por categoría
    y = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(13);
    doc.setFont('helvetica','bold');
    doc.text('GASTOS POR CATEGORÍA', 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [['Categoría', 'Total']],
      body: Object.entries(preview.porCategoria).map(([cat, total]) => [cat, fmt(total)]),
      theme: 'grid',
      headStyles: { fillColor: [192,57,43], textColor:255, fontStyle:'bold' },
      alternateRowStyles: { fillColor: [253,219,216] },
      styles: { fontSize:10, cellPadding:4 },
      columnStyles: { 1: { halign:'right' } },
    });

    // Resumen por día
    y = doc.lastAutoTable.finalY + 12;
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(13);
    doc.setFont('helvetica','bold');
    doc.text('DETALLE POR DÍA', 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [['Fecha', 'Ingresos', 'Gastos', 'Balance']],
      body: preview.porFecha.map(d => [
        new Date(d.fecha+'T12:00:00').toLocaleDateString('es-AR'),
        fmt(d.ingresos), fmt(d.gastos), fmt(d.balance)
      ]),
      theme: 'grid',
      headStyles: { fillColor: [27,38,49], textColor:255, fontStyle:'bold' },
      alternateRowStyles: { fillColor: [240,244,248] },
      styles: { fontSize:9, cellPadding:3 },
      columnStyles: { 1:{halign:'right'}, 2:{halign:'right'}, 3:{halign:'right', fontStyle:'bold'} },
    });

    // Footer
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Página ${i} de ${pages}`, pageW/2, doc.internal.pageSize.getHeight()-8, { align:'center' });
    }

    doc.save(`Reporte_${fechaDesde}_al_${fechaHasta}.pdf`);
  }

  return (
    <div style={{ minHeight:'100vh', background:'#F2F3F4' }}>
      <div style={{ background:'#1B2631', padding:'16px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1 style={{ color:'#fff', margin:0, fontSize:20 }}>📈 Reportes</h1>
          <p style={{ color:'#aaa', margin:'2px 0 0', fontSize:12 }}>Generá reportes en PDF</p>
        </div>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'transparent', border:'1px solid #555', color:'#aaa', padding:'8px 16px', borderRadius:8, cursor:'pointer' }}>
          ← Volver
        </button>
      </div>

      <div style={{ maxWidth:800, margin:'0 auto', padding:24 }}>

        {/* Selector de fechas */}
        <div style={{ background:'#fff', borderRadius:12, padding:20, marginBottom:20, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin:'0 0 16px', fontSize:15, color:'#1B2631' }}>📅 Seleccioná el período</h2>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div>
              <label style={{ fontSize:12, color:'#888', fontWeight:'bold', display:'block', marginBottom:4 }}>Desde</label>
              <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
                style={{ padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14 }} />
            </div>
            <div>
              <label style={{ fontSize:12, color:'#888', fontWeight:'bold', display:'block', marginBottom:4 }}>Hasta</label>
              <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
                style={{ padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14 }} />
            </div>
            <button onClick={cargarDatos} disabled={loading}
              style={{ padding:'9px 24px', background:'#1B2631', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:'bold', fontSize:14 }}>
              {loading ? 'Cargando...' : '🔍 Ver Reporte'}
            </button>
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <>
            {/* KPIs */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
              {[
                { label:'💰 Ingresos', valor: preview.totalVentas, color:'#27AE60' },
                { label:'💸 Gastos',   valor: preview.totalGastos,  color:'#C0392B' },
                { label:'📊 Balance',  valor: preview.balance,      color:'#1B2631' },
              ].map((k,i) => (
                <div key={i} style={{ background:'#fff', borderRadius:12, padding:16, textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize:12, color:'#888', marginBottom:4 }}>{k.label}</div>
                  <div style={{ fontSize:20, fontWeight:'bold', color:k.color }}>{fmt(k.valor)}</div>
                </div>
              ))}
            </div>

            {/* Medios de pago */}
            <div style={{ background:'#fff', borderRadius:12, padding:20, marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin:'0 0 12px', fontSize:14, color:'#1B2631' }}>💳 Por Medio de Pago</h3>
              {Object.entries(preview.medios).map(([medio, total]) => (
                <div key={medio} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f5f5f5', fontSize:13 }}>
                  <span style={{ color:'#555', textTransform:'capitalize' }}>{medio}</span>
                  <span style={{ fontWeight:'bold', color:'#27AE60' }}>{fmt(total)}</span>
                </div>
              ))}
            </div>

            {/* Botón PDF */}
            <button onClick={generarPDF}
              style={{ width:'100%', padding:16, background:'#C0392B', color:'#fff', border:'none', borderRadius:12, fontSize:16, fontWeight:'bold', cursor:'pointer' }}>
              📄 Descargar PDF
            </button>
          </>
        )}
      </div>
    </div>
  );
}